import { useEffect, useState } from 'react'
import Button from '../../../components/ui/Button.jsx'
import Icon from '../../../components/ui/Icon.jsx'
import api from '../../../lib/api.js'

/*
 * Documents step — inventory §5. Upload the legal documents required to validate
 * the company: bce_extract (required), id_card (required), procuration
 * (optional). POST /register/upload-document (multipart, max 5 MB each). Already
 * uploaded documents are reflected from GET /register/status.
 */
const MAX = 5 * 1024 * 1024
const DOCS = [
  { type: 'bce_extract', required: true },
  { type: 'id_card', required: true },
  { type: 'procuration', required: false },
]

export default function DocumentsStep({ t, onBack, onDone }) {
  const [done, setDone] = useState({}) // type -> filename (already uploaded or just uploaded)
  const [picked, setPicked] = useState({}) // type -> File
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    api.get('/register/status').then(({ data }) => {
      const d = {}
      for (const doc of data?.documents || []) d[doc.type || doc.document_type] = doc.filename || doc.original_filename || true
      setDone(d)
    }).catch(() => {})
  }, [])

  const pick = (type, file) => {
    setError('')
    if (!file) return
    if (file.size > MAX) { setError(t('register.documents.tooBig')); return }
    setPicked((p) => ({ ...p, [type]: file }))
  }

  const hasRequired = DOCS.filter((d) => d.required).every((d) => done[d.type] || picked[d.type])

  const submit = async () => {
    setBusy(true); setError('')
    try {
      for (const d of DOCS) {
        const file = picked[d.type]
        if (!file) continue
        const fd = new FormData()
        fd.append('document_type', d.type)
        fd.append('file', file)
        await api.post('/register/upload-document', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      }
      onDone()
    } catch (e) { setError(e?.response?.data?.error || t('register.documents.uploadError')) } finally { setBusy(false) }
  }

  return (
    <div>
      <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 15, color: 'var(--color-ink)', margin: '6px 0 12px' }}>{t('register.documents.title')}</h3>
      <p style={{ color: 'var(--color-text)', fontSize: 14, marginBottom: 18 }}>{t('register.documents.intro')}</p>

      {error && <div role="alert" style={{ background: 'var(--color-danger-bg)', color: 'var(--color-danger)', border: '1px solid #f3c9c9', borderRadius: 10, padding: '9px 12px', fontSize: 13, marginBottom: 14 }}>{error}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
        {DOCS.map((d) => {
          const uploaded = done[d.type]
          const file = picked[d.type]
          const ok = uploaded || file
          return (
            <div key={d.type} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 14, border: '1px solid var(--color-border)', borderRadius: 12, background: ok ? 'var(--color-success-bg)' : '#fff' }}>
              <span style={{ color: ok ? 'var(--color-success)' : 'var(--color-text-muted)' }}><Icon name={ok ? 'check' : 'invoice'} size={20} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: 'var(--color-ink)', fontSize: 14 }}>{t(`register.documents.types.${d.type}`)} {d.required ? <span style={{ color: 'var(--color-danger)' }}>*</span> : <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>({t('register.documents.optional')})</span>}</div>
                <div style={{ fontSize: 12.5, color: 'var(--color-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file ? file.name : uploaded && uploaded !== true ? uploaded : uploaded ? t('register.documents.uploaded') : t('register.documents.hint')}</div>
              </div>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 38, padding: '0 14px', borderRadius: 'var(--radius-field)', border: '1.5px solid var(--color-border-alt)', background: '#fff', color: 'var(--color-ink)', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}>
                <Icon name="download" size={16} /> {uploaded || file ? t('register.documents.replace') : t('register.documents.choose')}
                <input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => { pick(d.type, e.target.files?.[0]); e.target.value = '' }} style={{ display: 'none' }} />
              </label>
            </div>
          )
        })}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="secondary" onClick={onBack}>{t('register.back')}</Button>
        <Button variant="primary" icon="chevron-right" loading={busy} disabled={!hasRequired} onClick={submit}>{t('register.continue')}</Button>
      </div>
    </div>
  )
}
